import React, { useRef, useEffect, useState } from 'react';
import * as echarts from 'echarts/core';
import { RadarChart } from 'echarts/charts';
import { SVGRenderer } from 'echarts/renderers';
import { GridComponent, TooltipComponent, TitleComponent, LegendComponent } from 'echarts/components';
// 标签自动布局、全局过渡动画等特性
import { LabelLayout, UniversalTransition } from 'echarts/features';
// import { themeColor } from '../../config-v2';
import { EChartsOption } from 'echarts';
import styles from './index.module.less';
import { unstable_batchedUpdates } from 'react-dom';
import cs from 'classnames';
import { throttle } from '@lhb/func';

const themeColor = {
  blue: ['#358AFF', '#39D3F9', '#FFA140', '#5366F6', '#80D6EB', '#FFEB58', '#A896EF'],
  blueRGB: [
    '53,138,255',
    '57,211,249',
    '255,161,64',
    '83,102,246',
    '128,214,235',
    '252,231,78',
    '168,150,239',
  ],
  blueGradient: ['#AED0FF', '#96C2FF', '#7EB4FF', '#65A6FF', '#4E98FF', '#358AFF'], // 漏斗图中的渐变主题色
  green: ['#52CBD1', '#7BD2DE', '#FFD190', '#86CCFF', '#9FDEF4', '#FFBA96', '#FF8A8A'], // 按组件库风格原先第一个颜色是'#0BB8AF'，这里因ui设计修改掉
  greenRGB: [
    '82,203,209', // 按组件库风格原先第一个颜色是'11,184,175'，这里因ui设计修改掉
    '123,210,222',
    '255,209,144',
    '134,204,255',
    '159,222,244',
    '255,186,150',
    '255,138,138',
  ],
  greenGradient: ['#9DE3DF', '#80DAD5', '#63D2CC', '#45C9C2', '#29C1B9', '#0BB8AF'], // 漏斗图中的渐变主题色
  purple: ['#7087E4', '#C096E0', '#F3D296', '#F99090', '#BFC7FC', '#9298E9', '#FDB791'],
  purpleRGB: [
    '112,135,228',
    '192,150,224',
    '243,210,150',
    '249,144,144',
    '191,199,252',
    '146,152,233',
    '253,183,145',
  ],
  purpleGradient: ['#D3D6F6', '#C6C9F3', '#B9BDF1', '#ACB1EE', '#9FA5EC', '#9298E9'], // 漏斗图中的渐变主题色
};
echarts.use([
  SVGRenderer,
  RadarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  LabelLayout,
  UniversalTransition,
]);

export interface V2RadarChartProps {
  /**
   * @description 图表标题
   */
  title?: string;
  /**
   * @description 图表主题
   */
  theme?: 'blue' | 'green' | 'purple';
  /**
   * @description 图表宽度，默认不指定，跟随外容器
   */
  width?: number | string;
  /**
   * @description 图表高度
   * @default 270px
   */
  height?: number | string;
  /**
   * @description 雷达宽度占总宽度百分比
   * @default "56%"
   */
  radarRadius?: number | string;
  /**
   * @description 雷达图绘制类型
   * @default polygon
   */
  shape?: 'circle' | 'polygon';
  /**
   * @description 是否跟随外容器自动缩放
   * @default false
   */
  resizable?: boolean;
  /**
   * @description 雷达图的指标 更多配置见 https://echarts.apache.org/zh/option.html#radar.indicator
   */
  indicator: any[];
  /**
   * @description 雷达图数据 更多配置见 https://echarts.apache.org/zh/option.html#series-radar.data
   */
  seriesData: any[];
  /**
   * @description echarts更多配置 详情见https://echarts.apache.org/zh/option.html#title
   * @type EChartsOption
   */
  config?: EChartsOption;
  /**
   * @description 数值字体大小
   */
  valueFontSize?: number;
  /**
   * @description 图表外层包装容器的类名
   */
  wrapperClassName?: string;
}

/**
* @description 便捷文档地址
* @see https://reactmobile.lanhanba.net/charts/v2-radar-chart
*/

const SiteMapRadarChart: React.FC<V2RadarChartProps> = (props) => {
  const {
    title,
    theme = 'blue',
    width = '100%',
    height = 270,
    radarRadius = '56%',
    shape = 'polygon',
    resizable = false,
    indicator = [],
    seriesData = [],
    config = {},
    valueFontSize = 14,
    wrapperClassName,
  } = props;
  const { truncationCount } = config || {};
  const chartRef = useRef<any>(null);
  const [echartsInstance, setEchartsInstance] = useState<any>(null);

  const [isConfig, setIsConfig] = useState(false); // options 是否配置完成
  const [options, setOptions] = useState<any>({
    ...config,
    title: {
      show: !!title,
      text: title || '',
      textStyle: {
        color: '#222222',
        fontWeight: 500,
        fontFamily: 'PingFangSC-Medium, PingFang SC',
        fontSize: 16,
        lineHeight: 22,
      },
      top: 6,
      left: 6,
      ...(config.title || {}),
    },
    legend: {
      ...(config.legend || {}),
    },
    color: themeColor[theme],
    radar: {
      shape: shape,
      radius: radarRadius,
      center: ['50%', indicator?.length % 2 ? '54%' : '50%'], // 奇数个指标时正上位置有指标正下没有，所以竖直方向中心略微往下;偶数个指标时正上正下都有指标显示，所以竖直居中;
      nameGap: (truncationCount as number) < 5 ? 5 : 8,
      splitNumber: 5,
      axisName: {
        padding: [0, 10, 0, 10],
        // 外圈展示
        formatter: (value: string, indicator: any) => {
          let shortName = value;
          // 截断长名称（保留前truncationCount字），加省略号
          if (truncationCount) {
            shortName = value.length > (truncationCount as number) ? value.slice(0, (truncationCount as number)) + '...' : value;
          }
          // const shortName = value.length > 5 ? value.slice(0, 5) + '...' : value;
          // 保留两行结构
          return [
            '{a|' + shortName + '}',
            '{b|' + indicator.value + '}',
          ].join('\n');
          // return [
          //   '{a|' + value + '}',
          //   '{b|' + indicator.value + '}',
          // ].join('\n');
        },
        lineHeight: 16,
        color: '#999999',
        rich: {
          a: {
            color: '#999999',
            fontFamily: 'PingFangSC-Regular',
            fontWeight: 300,
            fontSize: 11
          },
          b: {
            color: '#222222',
            fontFamily: 'PingFangSC-Regular, PingFang SC',
            fontWeight: 700,
            fontSize: valueFontSize,
            align: 'center',
            lineHeight: 24, // 为了与上方标题保持一定间距
          },
        },
      },
      axisLine: {
        lineStyle: {
          color: '#EEEEEE',
          width: 1,
        },
      },
      splitLine: {
        lineStyle: {
          type: [4, 4],
        }
      },
      splitArea: {
        show: false,
      },
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#222',
      borderColor: '#222',
      borderWidth: 0,
      textStyle: {
        color: '#fff',
        fontWeight: 'normal',
        fontSize: 12,
        fontFamily: 'PingFangSC-Regular, PingFang SC',
      },
      confine: true,
      ...(config.tooltip || {}),
    },
    series: [],
  });

  const buildSeries = (dataItem: any, dataIndex: number) => {
    const data = dataItem.value;
    const helper = data.map((item: any, index: number) => {
      const arr = new Array(data.length);
      arr.splice(index, 1, item);
      return arr;
    });
    return [data, ...helper].map((item, index) => {
      return {
        name: dataItem.name,
        type: 'radar',
        symbol: index === 0 ? 'circle' : 'none',
        symbolSize: 0,
        areaStyle: {
          color: `rgba(${themeColor[`${theme}RGB`][dataIndex]}, 0.1)`,
        },
        lineStyle: {
          color: index === 0 ? themeColor[theme][dataIndex] : 'transparent'
        },
        tooltip: {
          show: index !== 0,
          formatter: () => {
            return null;
          }
        },
        z: index === 0 ? 1 : 2,
        data: [item]
      };
    });
  };

  useEffect(() => {
    if (indicator?.length && seriesData?.length) {
      const series: any = [];
      seriesData.forEach((item, index) => {
        series.push(...buildSeries(item, index));
      });
      unstable_batchedUpdates(() => {
        setOptions({
          ...options,
          series,
          radar: {
            ...options.radar,
            indicator: [...indicator]
          },
        });
        setIsConfig(true);
      });
    }
  }, [indicator, seriesData]);

  useEffect(() => {
    if (!isConfig) return;
    let chart: any;
    if (chartRef.current) {
      chart = echarts.init(chartRef.current, 'light', {
        renderer: 'svg',
        height,
      });
      chart.setOption(options);
      setEchartsInstance(chart);
    }
    return () => chart?.dispose();
  }, [options, isConfig]);

  useEffect(() => {
    if (echartsInstance && resizable) {
      window.addEventListener('resize', throttle(() => {
        echartsInstance.resize(); // 图表根据窗口大小自适应
      }, 300));
    }
    return () => {
      if (resizable) {
        window.removeEventListener('resize', throttle(() => {
          echartsInstance.resize();
        }, 300));
      }
    };
  }, [echartsInstance, resizable]);

  return (
    <div className={cs(styles.V2RadarChartWrap, wrapperClassName)} style={{ width, height }}>
      <div ref={chartRef} className={styles.V2RadarChart}></div>
    </div>
  );
};

export default SiteMapRadarChart;
